import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Star } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// Compact "add TrainingPeaks Premium" upgrade CTA + dialog, reused beside every TP connect spot.
export default function TpPremiumUpgrade({ className = "", variant = "link" }) {
  const { user, refreshUser } = useAuth();
  const [open, setOpen] = useState(false);
  const [tpEmail, setTpEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const tpp = user?.tpPremium || null;
  const active = tpp && ["pending", "active", "cancelling"].includes(tpp.status);

  const subscribe = async () => {
    if (!tpEmail.trim()) return;
    setBusy(true);
    try {
      await api.post("/tp-premium/subscribe", { tp_email: tpEmail.trim() });
      await refreshUser();
      setTpEmail("");
      setOpen(false);
      toast.success("TrainingPeaks Premium added — we'll set you up within 24h");
    } catch (err) { toast.error(apiError(err, "Could not add TrainingPeaks Premium")); }
    finally { setBusy(false); }
  };

  if (active) {
    return (
      <p data-testid="tpp-active-note" className={`text-[11px] text-[#34C759] flex items-center gap-1 ${className}`}>
        <Star className="h-3 w-3" />
        {tpp.status === "active" ? "TrainingPeaks Premium active" : tpp.status === "pending" ? "TrainingPeaks Premium requested — setting you up" : "TrainingPeaks Premium ending at period end"}
      </p>
    );
  }

  return (
    <>
      {variant === "cta" ? (
        <Button type="button" data-testid="tpp-upgrade-open" onClick={() => setOpen(true)}
          className={`rounded-none bg-[#E2E30A] hover:bg-[#c9ca09] text-black text-xs uppercase font-semibold shrink-0 ${className}`}>
          <Star className="h-3.5 w-3.5 mr-1.5" /> Add Premium — £9.99/mo
        </Button>
      ) : (
        <button type="button" data-testid="tpp-upgrade-open" onClick={() => setOpen(true)}
          className={`text-[11px] uppercase tracking-wider text-[#FFB454] hover:text-[#ffca80] underline underline-offset-2 inline-flex items-center gap-1 ${className}`}>
          <Star className="h-3 w-3" /> No Premium? Add it — £9.99/mo
        </button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="tpp-upgrade-dialog" className="bg-[#0A0A0A] border-white/10 rounded-none text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight flex items-center gap-2">
              <Star className="h-4 w-4 text-[#E2E30A]" /> TrainingPeaks Premium — £9.99/mo
            </DialogTitle>
            <DialogDescription className="text-zinc-400 text-xs">
              We set you up as a Premium athlete inside our TrainingPeaks coaching account — Calendar Sync included. Billed as an extra £9.99/mo line on your SPC subscription.
            </DialogDescription>
          </DialogHeader>
          <p className="text-[11px] text-zinc-300 -mt-1">We'll add you within 24h — you'll get an invite email from TrainingPeaks to accept. Cancel anytime; it ends at your period end.</p>
          <Input data-testid="tpp-upgrade-email" type="email" value={tpEmail} onChange={(e) => setTpEmail(e.target.value)}
            placeholder="Your TrainingPeaks account email" className="bg-black border-white/15 text-white text-sm" />
          <Button data-testid="tpp-upgrade-submit" onClick={subscribe} disabled={busy || !tpEmail.trim()}
            className="rounded-none bg-[#E2E30A] hover:bg-[#c9ca09] text-black text-xs uppercase font-semibold w-full">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add Premium — £9.99/mo"}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
